import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Snowflake, Leaf, PartyPopper, Save } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';


interface SeasonalSettings {
  snow_enabled: boolean;
  snow_intensity: 'light' | 'medium' | 'heavy';
  leaves_enabled: boolean;
  leaves_intensity: 'light' | 'medium' | 'heavy';
  confetti_enabled: boolean;
  confetti_intensity: 'light' | 'medium' | 'heavy';
}

const SeasonalSettingsManagement = () => {
  const { toast } = useToast();
  const [settings, setSettings] = useState<SeasonalSettings>({
    snow_enabled: false,
    snow_intensity: 'medium',
    leaves_enabled: false,
    leaves_intensity: 'medium',
    confetti_enabled: false,
    confetti_intensity: 'medium',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('site_settings')
        .select('setting_value')
        .eq('setting_key', 'seasonal_effects')
        .maybeSingle();

      if (error) throw error;

      if (data?.setting_value) {
        const value = data.setting_value as unknown as SeasonalSettings;
        setSettings({
          snow_enabled: value.snow_enabled ?? false,
          snow_intensity: value.snow_intensity ?? 'medium',
          leaves_enabled: value.leaves_enabled ?? false,
          leaves_intensity: value.leaves_intensity ?? 'medium',
          confetti_enabled: value.confetti_enabled ?? false,
          confetti_intensity: value.confetti_intensity ?? 'medium',
        });
      }
    } catch (error: any) {
      console.error('Error loading seasonal settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const { data: existing } = await supabase
        .from('site_settings')
        .select('id')
        .eq('setting_key', 'seasonal_effects')
        .maybeSingle();

      const settingsJson = JSON.parse(JSON.stringify(settings));
      
      if (existing) {
        const { error } = await supabase
          .from('site_settings')
          .update({ setting_value: settingsJson })
          .eq('setting_key', 'seasonal_effects');
        
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('site_settings')
          .insert([{ 
            setting_key: 'seasonal_effects', 
            setting_value: settingsJson
          }]);
        
        if (error) throw error;
      }

      toast({
        title: 'Settings saved',
        description: 'Seasonal effects settings have been updated.',
      });
    } catch (error: any) {
      toast({
        title: 'Error saving settings',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-muted rounded w-1/3"></div>
            <div className="h-10 bg-muted rounded"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Snowflake className="w-5 h-5" />
          Seasonal Effects
        </CardTitle>
        <CardDescription>
          Configure seasonal visual effects for the website homepage
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">

        {/* Snow Effect */}
        <div className="border rounded-lg p-4 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Snowflake className="w-5 h-5 text-blue-400" />
              <div className="space-y-0.5">
                <Label htmlFor="snow-toggle">Winter Snow</Label>
                <p className="text-sm text-muted-foreground">
                  Falling snowflakes for the winter season
                </p>
              </div>
            </div>
            <Switch
              id="snow-toggle"
              checked={settings.snow_enabled}
              onCheckedChange={(checked) => 
                setSettings({ 
                  ...settings, 
                  snow_enabled: checked,
                  leaves_enabled: checked ? false : settings.leaves_enabled,
                  confetti_enabled: checked ? false : settings.confetti_enabled,
                })
              }
            />
          </div>

          {settings.snow_enabled && (
            <div className="space-y-2 pl-7">
              <Label htmlFor="snow-intensity">Intensity</Label>
              <Select
                value={settings.snow_intensity}
                onValueChange={(value: 'light' | 'medium' | 'heavy') =>
                  setSettings({ ...settings, snow_intensity: value })
                }
              >
                <SelectTrigger id="snow-intensity" className="w-full">
                  <SelectValue placeholder="Select intensity" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="light">Light (30 snowflakes)</SelectItem>
                  <SelectItem value="medium">Medium (60 snowflakes)</SelectItem>
                  <SelectItem value="heavy">Heavy (100 snowflakes)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {/* Leaves Effect */}
        <div className="border rounded-lg p-4 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Leaf className="w-5 h-5 text-orange-500" />
              <div className="space-y-0.5">
                <Label htmlFor="leaves-toggle">Autumn Leaves</Label>
                <p className="text-sm text-muted-foreground">
                  Falling leaves for the autumn season
                </p>
              </div>
            </div>
            <Switch
              id="leaves-toggle"
              checked={settings.leaves_enabled}
              onCheckedChange={(checked) => 
                setSettings({ 
                  ...settings, 
                  leaves_enabled: checked,
                  snow_enabled: checked ? false : settings.snow_enabled,
                  confetti_enabled: checked ? false : settings.confetti_enabled,
                })
              }
            />
          </div>

          {settings.leaves_enabled && (
            <div className="space-y-2 pl-7">
              <Label htmlFor="leaves-intensity">Intensity</Label>
              <Select
                value={settings.leaves_intensity}
                onValueChange={(value: 'light' | 'medium' | 'heavy') =>
                  setSettings({ ...settings, leaves_intensity: value })
                }
              >
                <SelectTrigger id="leaves-intensity" className="w-full">
                  <SelectValue placeholder="Select intensity" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="light">Light (20 leaves)</SelectItem>
                  <SelectItem value="medium">Medium (40 leaves)</SelectItem>
                  <SelectItem value="heavy">Heavy (70 leaves)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {/* Confetti Effect */}
        <div className="border rounded-lg p-4 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <PartyPopper className="w-5 h-5 text-pink-500" />
              <div className="space-y-0.5">
                <Label htmlFor="confetti-toggle">Celebration Confetti</Label>
                <p className="text-sm text-muted-foreground">
                  Festive confetti for special occasions
                </p>
              </div>
            </div>
            <Switch
              id="confetti-toggle"
              checked={settings.confetti_enabled}
              onCheckedChange={(checked) => 
                setSettings({ 
                  ...settings, 
                  confetti_enabled: checked,
                  snow_enabled: checked ? false : settings.snow_enabled,
                  leaves_enabled: checked ? false : settings.leaves_enabled,
                })
              }
            />
          </div>

          {settings.confetti_enabled && (
            <div className="space-y-2 pl-7">
              <Label htmlFor="confetti-intensity">Intensity</Label>
              <Select
                value={settings.confetti_intensity}
                onValueChange={(value: 'light' | 'medium' | 'heavy') =>
                  setSettings({ ...settings, confetti_intensity: value })
                }
              >
                <SelectTrigger id="confetti-intensity" className="w-full">
                  <SelectValue placeholder="Select intensity" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="light">Light (40 pieces)</SelectItem>
                  <SelectItem value="medium">Medium (80 pieces)</SelectItem>
                  <SelectItem value="heavy">Heavy (150 pieces)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <Button onClick={saveSettings} disabled={saving} className="w-full sm:w-auto">
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </CardContent>
    </Card>
  );
};

export default SeasonalSettingsManagement;
